import { Component, HostListener, inject, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subject, firstValueFrom } from 'rxjs';
import { LocalStorageService } from './connectors/localstorage';
import { BehaviorSubject } from 'rxjs';
import { UserBehaviorService } from './services/user-events/user-behavior.service';
import { LoginService } from './services/login.service';
import { UsersService } from './services/users.service';
import { CryptService } from './services/crypt.service';
import { distinctUntilChanged, take, takeUntil, filter, map, switchMap, timeout } from 'rxjs/operators';
import { ActivityService } from './services/auth/activityService.service';
import { AuthService } from './services/auth/authService.service';
import { Router, NavigationEnd, RouterOutlet, ActivatedRoute } from '@angular/router';
import { HeaderComponent } from './home/shared/components/header/header.component';
import { SideMenuComponent } from './home/shared/components/side-menu/side-menu.component';
import { MenuStateService } from './home/core/services/menu-state.service';
import { MenuBootstrapService } from './home/core/services/menu-bootstrap.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { NotificationsService } from '@core/notifications/services/notifications.service';
import { PageTitleService } from '@core/services/page-title.service';
import { BroadcastChannelService } from './services/broadcast-channel.service';
import { OverlayContainer } from '@angular/cdk/overlay';
import { MsalService } from '@azure/msal-angular';
import { MsalBootstrapService } from './services/auth/msal-bootstrap.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: true,
  imports: [SideMenuComponent, RouterOutlet, HeaderComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  readonly menuStateService = inject(MenuStateService);
  readonly menuBootstrapService = inject(MenuBootstrapService);
  readonly notificationService = inject(NotificationsService);
  readonly #msalService = inject(MsalService);
  readonly #msalBootstrapService = inject(MsalBootstrapService);
  isMenuOpen = toSignal(this.menuStateService.isMenuOpen$, { initialValue: false });
  isLegacyRoute = true;

  globalListenFunc!: Function;

  url: string = this.document.location.href;
  loginPath: boolean = false;

  base_url = window.location.origin;
  pathName = window.location.pathname.split('/');

  loaderSpin: boolean = false;

  timeLeft: number = 20000;
  interval: any;

  user: any;
  email: any;

  innerWidth: any;
  websocketUrl: any;

  downloadable: boolean = false;

  previousUrl: string = '';

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private localstorage: LocalStorageService,
    private renderer: Renderer2,
    private userBehavior: UserBehaviorService,
    private loginService: LoginService,
    private userService: UsersService,
    private crypt: CryptService,
    private activityService: ActivityService,
    private tokenLogin: AuthService,
    private pageTitleService: PageTitleService,
    private broadcastChannelService: BroadcastChannelService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private overlayContainer: OverlayContainer
  ) {}

  private syncOverlayClass(isLegacy: boolean): void {
    const el = this.overlayContainer.getContainerElement();
    if (isLegacy) {
      el.classList.add('ntt-basecoat-legacy');
      el.classList.remove('new-ui');
    } else {
      el.classList.remove('ntt-basecoat-legacy');
      el.classList.add('new-ui');
    }
  }

  ngOnInit(): void {
    void this.#msalBootstrapService.ensureReady(this.#msalService, this.tokenLogin);

    this.menuBootstrapService.initialize();

    // Validate access when user logs in
    this.userBehavior.selectedUser.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe((user) => {
      if (user && user?.email) {
        this.email = user?.email;

        void this.validateAccess();
      }

      this.chartUrl(window.location.pathname);
    });

    this.userBehavior.localStorageEvents$
      .pipe(
        filter((event) => event.userId),
        take(1),
        switchMap(() =>
          this.tokenLogin.loginCompleted$.pipe(
            filter((completed) => completed),
            take(1)
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const userId = Number(this.localstorage.decryptAndGet('userId'));
        this.notificationService.initialize(userId);
      });

    this.globalListenFunc = this.renderer.listen('document', 'mousemove', (e) => {
      this.expiring();
    });

    this.globalListenFunc = this.renderer.listen('document', 'touchstart', (e) => {
      this.expiring();
    });

    this.globalListenFunc = this.renderer.listen('document', 'touchmove', (e) => {
      this.expiring();
    });

    this.globalListenFunc = this.renderer.listen('document', 'touchend', (e) => {
      this.expiring();
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: any) => {
        this.chartUrl(event.urlAfterRedirects);
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) route = route.firstChild;
          return route.snapshot.data;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => {
        this.isLegacyRoute = !data['newUI'];
        // Sprint 3: keep CDK overlay portal class in sync with the active route type.
        this.syncOverlayClass(this.isLegacyRoute);
      });

    // Seed the overlay class for the initial route (before any NavigationEnd fires).
    {
      let route = this.activatedRoute;
      while (route.firstChild) route = route.firstChild;
      this.syncOverlayClass(!route.snapshot.data['newUI']);
    }

    // Inicialização baseada no path atual
    this.chartUrl(window.location.pathname);

    /** Listen for successful login messages from other tabs and reload if on login route */
    this.broadcastChannelService.loginSuccess$
      .pipe(
        filter((message) => message !== null && Date.now() - message.timestamp < 5000), // only react to recent login messages to avoid unnecessary reloads
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const pathName = window.location.pathname.split('/');
        if (pathName[1] === 'login') {
          window.location.reload();
        }
      });

    /** Listen for logout initiated messages from other tabs and trigger local logout */
    this.broadcastChannelService.logoutInitiated$
      .pipe(
        filter((message) => message !== null && Date.now() - message.timestamp < 5000),
        takeUntil(this.destroy$)
      )
      .subscribe((message) => {
        const pathName = window.location.pathname.split('/');
        if (pathName[1] !== 'login' && pathName[1] !== '') {
          this.tokenLogin.logout(message!.reason, true); // true = skip broadcast to avoid loops
        }
      });
  }

  async validateAccess() {
    const authenticated = await this.#ensureExchangeCompleted();
    if (!authenticated) {
      return;
    }

    this.profileCheck();
    this.#fetchInternalUserId();
  }

  async #ensureExchangeCompleted(): Promise<boolean> {
    const user = await this.userBehavior.fromBehavior();
    if (!user?.email) {
      return false;
    }

    if (localStorage.getItem('mlct')) {
      this.tokenLogin.restoreSessionIfValid();
      this.activityService.setupActivityListeners();
      return true;
    }

    const azureIdToken = await this.#getAzureIdToken();
    if (!azureIdToken) {
      return false;
    }

    this.activityService.setupActivityListeners();
    this.tokenLogin.login(user.email, azureIdToken);

    try {
      await firstValueFrom(
        this.tokenLogin.loginCompleted$.pipe(
          filter((completed) => completed),
          take(1),
          timeout(15000)
        )
      );
      return true;
    } catch {
      return false;
    }
  }

  #fetchInternalUserId(): void {
    if (!this.email) {
      return;
    }

    this.userService.getUsersByEmail(this.email).subscribe({
      next: (resp: any) => {
        if (!resp || !Array.isArray(resp) || resp.length === 0 || !resp[0].userId) {
          return;
        }

        const cryptedUser = this.crypt.encryptBy(resp[0].userId.toString());

        if (cryptedUser) {
          this.localstorage.set('userId', [cryptedUser]);

          this.userBehavior.emitUserIdEvent(true);
        }
      }
    });
  }

  profileCheck() {
    this.userService.getProfile(this.email).subscribe((resp) => {
      let cryptedUser = '';
      if (resp.profile.length > 0) {
        cryptedUser = this.crypt.encryptArray(resp.profile);
      } else {
        cryptedUser = this.crypt.encryptBy(resp.profile);
      }

      if (cryptedUser) this.localstorage.set('profile', [cryptedUser]);
    });
  }

  chartUrl(url: string) {
    if (!url) return;

    let path = '';
    try {
      // Se for uma URL completa, extrai o pathname
      if (url.startsWith('http')) {
        path = new URL(url).pathname;
      } else {
        // Se for apenas um path, remove query params e fragmentos
        path = url.split(/[?#]/)[0];
      }
    } catch (e) {
      path = url;
    }

    path = path.toLowerCase();

    // Define se estamos em uma página de login ou na raiz (sem sistema logado)
    // Usamos regex ou verificações exatas para evitar falsos positivos
    if (path.includes('/login') || path === '/' || path === '') {
      this.loginPath = true;
    } else {
      this.loginPath = false;
    }
  }

  setLoader(url: string) {
    if (
      url.includes('/projects/details') ||
      url.includes('/teams') ||
      url.includes('/roles') ||
      url.includes('/users')
    ) {
      this.loaderSpin = !this.loaderSpin;

      setTimeout(() => {
        this.loaderSpin = false;
      }, 8000);
    }
  }

  expiring(expire?: boolean) {
    const subject = new BehaviorSubject(this.timeLeft);

    if (!expire) {
      this.timeLeft = 20000;
      this.startTimer();
    }

    if (subject.value == 0 || expire) {
      const local = this.localstorage.get('NTTUSLOGIN');
      if (local) {
        this.localstorage.remove('NTTUSLOGIN');
        this.loginService.expire(this.email, 'Y').subscribe();
      }
    }
  }

  startTimer() {
    this.timeLeft = 20000;
    this.interval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
        if (this.timeLeft == 0) {
          this.expiring(true);
        }
      } else {
        this.timeLeft = 20000;
      }
    }, 1000);
  }

  async #getAzureIdToken(): Promise<string | null> {
    try {
      await this.#msalBootstrapService.ensureReady(this.#msalService, this.tokenLogin);

      const cachedIdToken = this.tokenLogin.getAzureIdToken();
      if (cachedIdToken) {
        return cachedIdToken;
      }

      let account = this.#msalService.instance.getActiveAccount();

      if (!account) {
        const accounts = this.#msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          account = accounts[0];
          this.#msalService.instance.setActiveAccount(account);
        }
      }

      if (!account) {
        return null;
      }

      const result = await this.#msalService.instance.acquireTokenSilent({
        account,
        scopes: ['openid', 'profile', 'offline_access']
      });

      if (result.idToken) {
        this.tokenLogin.setAzureIdToken(result.idToken);
        return result.idToken;
      }

      return null;
    } catch {
      return null;
    }
  }

  @HostListener('window:resize', ['$event'])
  listenerWidth(event: any) {
    this.innerWidth = window.innerWidth;
    if (this.innerWidth > 1920) {
      location.reload();
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();

    this.globalListenFunc();
  }
}
